import { useState, useEffect } from 'react';
import { 
  Shield, 
  Check, 
  X, 
  Lock, 
  Info,
  ChevronRight,
  Eye,
  Settings,
  GitBranch,
  Package,
  DollarSign,
  BotMessageSquare,
  AlertCircle
} from 'lucide-react';
import { useRoles, usePermissions } from '@/api/queries';
import LoadingState from '../ui/LoadingState';
import ErrorState from '../ui/ErrorState';

interface FuncoesTabProps {
  organizationId?: string;
}

const ROLE_COLORS: Record<string, string> = {
  OWNER: 'indigo',
  ADMIN: 'amber',
  MANAGER: 'blue',
  MEMBER: 'slate',
  VIEWER: 'gray',
};

export default function FuncoesTab({ organizationId }: FuncoesTabProps) {
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const { data: roles = [], isLoading: rolesLoading, error: rolesError, refetch: refetchRoles } = useRoles(organizationId || '');
  const { data: permissions = [], isLoading: permsLoading, error: permsError, refetch: refetchPerms } = usePermissions();

  const selectedRole = roles.find((r: any) => r.id === selectedRoleId) || roles[0] || null;

  useEffect(() => {
    if (roles.length > 0 && !selectedRoleId) {
      setSelectedRoleId(roles[0].id);
    }
  }, [roles, selectedRoleId]);

  if (!organizationId) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-[#64748B] font-medium">
        Selecione uma organização para gerenciar funções e permissões.
      </div>
    );
  }

  if (rolesLoading || permsLoading) {
    return <LoadingState message="Carregando funções e permissões..." />;
  }

  if (rolesError || permsError) {
    return <ErrorState message={(rolesError || permsError)?.message || 'Erro ao carregar'} retry={() => { refetchRoles(); refetchPerms(); }} />;
  }

  const permissionList = [
    { id: 'manage_members', label: 'Gerenciar Membros', icon: Shield, desc: 'Convidar, desativar ou redefinir papéis de membros' },
    { id: 'create_projects', label: 'Criar Projetos', icon: GitBranch, desc: 'Adicionar novos quadros e roadmaps de projetos' },
    { id: 'edit_projects', label: 'Editar Projetos', icon: GitBranch, desc: 'Gerenciar backlog, kanbans e roadmaps' },
    { id: 'delete_projects', label: 'Excluir Projetos', icon: Lock, desc: 'Apagar permanentemente projetos e dados' },
    { id: 'view_finance', label: 'Visualizar Financeiro', icon: DollarSign, desc: 'Consultar receitas, despesas e fluxo de caixa' },
    { id: 'manage_finance', label: 'Gerenciar Financeiro', icon: DollarSign, desc: 'Lançar novas movimentações e faturas' },
    { id: 'manage_ai', label: 'Gerenciar IA', icon: BotMessageSquare, desc: 'Treinar e dar prompts para assistentes do workspace' },
    { id: 'manage_settings', label: 'Configurações do Workspace', icon: Settings, desc: 'Alterar nome, logo e domínios integrados' },
    { id: 'create_products', label: 'Criar Produtos', icon: Package, desc: 'Definir catálogos de produtos e entregas' },
    { id: 'publish_products', label: 'Publicar Produtos (Deploy)', icon: GitBranch, desc: 'Ativar releases e versões de entregáveis' },
  ];

  const getRolePermissions = (role: any): string[] => {
    if (!role) return [];
    if (role.key === 'OWNER' || role.key === 'ADMIN') return permissionList.map(p => p.id);
    return role.Permissions?.map((p: any) => p.Permission?.key || p.Permission?.id || p.id) || [];
  };

  return (
    <div className="flex flex-col gap-8 h-full animate-fade-in" id="funcoes-tab-container">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#111111] tracking-tight">Funções & Permissões</h3>
          <p className="text-sm text-[#64748B] font-medium">Defina o que cada membro pode visualizar e realizar no workspace</p>
        </div>
        <div className="bg-neutral-50 px-4 py-2 rounded-xl border border-neutral-100 flex items-center gap-2">
          <Lock size={14} className="text-neutral-500" />
          <span className="text-xs font-bold text-neutral-600 uppercase tracking-wider">{roles.length} Funções Ativas</span>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
        <div className="xl:col-span-1 flex flex-col gap-3">
          {roles.map((role: any) => {
            const isSelected = role.id === selectedRoleId;
            const color = ROLE_COLORS[role.key] || 'slate';
            return (
              <button
                key={role.id}
                onClick={() => setSelectedRoleId(role.id)}
                className={`flex flex-col gap-1.5 p-5 rounded-3xl border text-left transition-all cursor-pointer ${
                  isSelected 
                    ? 'bg-black border-black text-white shadow-xl shadow-black/10' 
                    : 'bg-white border-[#0F172A0A] text-[#111111] hover:border-black/10 shadow-[0_4px_20px_rgb(0,0,0,0.01)]'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-black tracking-tight">{role.name}</span>
                  <ChevronRight size={14} className={isSelected ? 'text-white/40' : 'text-[#94A3B8]'} />
                </div>
                <p className={`text-[11px] leading-relaxed font-semibold ${isSelected ? 'text-white/60' : 'text-[#64748B]'}`}>
                  {role.description || `Função ${role.name}`}
                </p>
                {role._count && (
                  <p className={`text-[10px] font-bold ${isSelected ? 'text-white/40' : 'text-[#94A3B8]'}`}>
                    {role._count.Members || 0} membros
                  </p>
                )}
              </button>
            );
          })}
        </div>

        <div className="xl:col-span-3 bg-white border border-[#0F172A0A] rounded-[40px] p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.02)] flex flex-col gap-8">
          {selectedRole ? (
            <>
              <div className="flex items-center gap-3 pb-6 border-b border-[#0F172A05]">
                <div className="w-12 h-12 rounded-2xl bg-[#FAFAFA] border border-[#0F172A05] flex items-center justify-center text-[#111111]">
                  <Shield size={24} className="text-indigo-600" />
                </div>
                <div>
                  <h4 className="text-lg font-black text-[#111111] tracking-tight">Permissões de {selectedRole.name}</h4>
                  <p className="text-xs text-[#64748B] font-semibold italic mt-0.5">{selectedRole.description || 'Sem descrição'}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                {permissionList.map((perm) => {
                  const rolePerms = getRolePermissions(selectedRole);
                  const isChecked = rolePerms.includes(perm.id) || rolePerms.includes('all');
                  return (
                    <div key={perm.id} className="flex items-start justify-between group">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                          isChecked ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-50 text-slate-300'
                        }`}>
                          <perm.icon size={18} />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-[#111111]">{perm.label}</span>
                          <span className="text-[11px] text-[#64748B] font-semibold">{perm.desc}</span>
                        </div>
                      </div>
                      {isChecked ? (
                        <div className="mt-1 w-6 h-6 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 flex-shrink-0">
                          <Check size={14} strokeWidth={3} />
                        </div>
                      ) : (
                        <div className="mt-1 w-6 h-6 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-300 flex-shrink-0">
                          <X size={14} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 p-6 bg-indigo-50/30 rounded-3xl border border-indigo-100/50 flex gap-4">
                <div className="w-10 h-10 rounded-2xl bg-white border border-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0">
                  <Info size={20} />
                </div>
                <div className="flex flex-col gap-1">
                  <h5 className="text-sm font-bold text-indigo-900 tracking-tight">Sobre as Permissões</h5>
                  <p className="text-xs text-indigo-800/70 font-medium leading-relaxed">
                    As permissões são cumulativas e baseadas na hierarquia do RBAC. Funções Owner e Admin têm acesso total.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-[#64748B] font-medium">Selecione uma função para ver as permissões</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
